import React, { useLayoutEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { cleanActions, getActions } from '@/store/actions/actions';
import { selectUser } from '@/store/selectors/user';

import useLanguage from '@/components/common/hooks/useLanguage';
import useToggle from '@/components/common/hooks/useToggle';
import ItemHeader from '@/components/actions/ItemHeader';
import EmptyCatalog from '@/components/actions/EmptyCatalog';
import Catalog from '@/components/teamActions/Catalog';
import Loader from '@/components/common/Loader';
import Navigation from '@/components/Navigation';
import { selectRecurrentActions } from '@/store/selectors/actions';
import { getCustomers, cleanCustomers } from '@/store/actions/customers';
import { getProjects, cleanProjects } from '@/store/actions/projects';
import { getUsersWithManagers, cleanUsers } from '@/store/actions/users';
import Empty from '@/components/Empty';

const TeamActionsUpcoming: React.FC = () => {
  const [isFilterActive, toggleIsFilterActive] = useToggle();
  const dispatch = useDispatch();
  const history = useHistory();
  const actions = useSelector(selectRecurrentActions);
  const user = useSelector(selectUser);

  // Derived in render, never copied into state by an effect: an effect runs with
  // the actions of the render that scheduled it, and the copy would outlive the
  // wipe that follows.
  const filteredActions = useMemo(() => (actions && isFilterActive
    ? actions.filter((action) => (
      action.type === 'team' && action.assign.includes(user!._id)
    ))
    : actions), [isFilterActive, actions]);

  const [{ common }] = useLanguage();

  // A layout effect, so the stored copies render before the first paint -- see
  // store/responses.ts.
  useLayoutEffect(() => {
    const getInfo = async () => {
      dispatch(getActions(user?.role));
      dispatch(getUsersWithManagers());
      dispatch(getProjects());
      dispatch(getCustomers());
    };
    if (!actions) {
      getInfo();
    }
    return () => {
      if (!history.location.pathname.includes('actions')) {
        dispatch(cleanActions());
        dispatch(cleanUsers());
        dispatch(cleanProjects());
        dispatch(cleanCustomers());
      }
    };
  }, []);

  const handleGoBack = () => {
    history.goBack();
  };

  return (
    <>
      <ItemHeader
        headerName={common.recurrent_actions}
        toggleFilter={toggleIsFilterActive}
        isFilterActive={isFilterActive}
        goBack={handleGoBack}
        isSales
      />
      <Empty height="72px" />
      {!filteredActions || !actions
        ? <Loader scale="0.5" />
        : (
          <ItemsContainer className="scrollbar">
            {filteredActions.length ? (
              <Catalog
                pageType="recurrent"
                actions={filteredActions}
              />
            ) : (
              <EmptyCatalog />
            )}
          </ItemsContainer>
        )}
      <Empty height="64px" />
      <Navigation path="actions" />
    </>
  );
};

const ItemsContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  position: relative;
  padding: 16px 0;
  overflow-y: auto;
  flex: 1;
`;

export default TeamActionsUpcoming;
